<template>
  <div v-if="hasOneOption">
    <dx-description-label v-if="$te(`${translationKeyPrefix}.description`)">
      {{ $t(`${translationKeyPrefix}.description`) }}
    </dx-description-label>
    <dl class="selection singleOption">
      <dt>{{ $t(`${translationKeyPrefix}.label`) }}</dt>
      <dd>{{ $t(`${mappedRadioOptions[0].name}`) }}</dd>
    </dl>
  </div>
  <label v-else-if="hasFewOptions" class="selection radioSelection">
    <dx-description-label v-if="$te(`${translationKeyPrefix}.description`)">
      {{ $t(`${translationKeyPrefix}.description`) }}
    </dx-description-label>
    <dx-description-label v-if="$te(`${translationKeyPrefix}.label`)">
      {{ $t(`${translationKeyPrefix}.label`) }}
    </dx-description-label>
    <dx-radio-buttons
      :id="config.id"
      :options="mappedRadioOptions"
      :error="translatedError"
      :value="value[0]"
      @change="update"
      @blur="onBlur"
    />
  </label>
  <div v-else>
    <dx-description-label v-if="$te(`${translationKeyPrefix}.description`)">
      {{ $t(`${translationKeyPrefix}.description`) }}
    </dx-description-label>

    <dx-select
      :value="value[0]"
      :label="translatedLabel"
      :options="mappedSelectOptions"
      :disabled="false"
      :error="translatedError"
      class="selection"
      @input="update"
      @blur="onBlur"
    />
  </div>
</template>

<script>
import {
  DxRadioButtons,
  DxSelect,
} from '@sumcumo/dextra-frontend-component-lib'
import DxDescriptionLabel from '~/components/DX-DescriptionLabel/DxDescriptionLabel'

export default {
  name: 'DXSelectAdapter',
  components: {
    DxDescriptionLabel,
    DxSelect,
    DxRadioButtons,
  },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Array,
      default: () => [],
    },
    error: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    translationKeyPrefix() {
      if (this.config.source) {
        if (this.config.source.kind === 'values') {
          return `attributes.${this.config.source.key}.${this.config.source.originalId}`
        }

        return `${this.config.source.key}.${this.config.source.originalId}`
      }

      return ''
    },

    mappedRadioError() {
      return this.error || ''
    },

    mappedSelectOptions() {
      const caption = {
        label: this.$t('selectOption'),
        caption: true,
      }
      const options = this.config.options.map((option) => ({
        value: option.value,
        label: this.$t(`${this.translationKeyPrefix}.options.${option.name}`),
      }))
      return [caption, ...options]
    },

    mappedRadioOptions() {
      return this.config.options.map((option) => ({
        value: option.value,
        name: this.$t(`${this.translationKeyPrefix}.options.${option.name}`),
      }))
    },

    hasFewOptions() {
      return this.config.options.length < 3
    },

    hasOneOption() {
      return this.config.options.length === 1
    },

    translatedLabel() {
      return this.$t(`${this.translationKeyPrefix}.label`)
    },

    translatedError() {
      return this.error
        ? this.$t(`errors.selectRadio.${this.error.type}`, {
            ...this.error.params,
            label: this.translatedLabel,
          })
        : null
    },

    translatedErrorOrEmpty() {
      return this.translatedError || ''
    },
  },
  watch: {
    hasOneOption: {
      handler(val) {
        if (val === true && this.config && this.config.options) {
          this.update(this.config.options[0].value)
        }
      },
      immediate: true,
    },
  },
  methods: {
    update(newValue) {
      this.$emit('input', [newValue])
    },
    onBlur() {
      this.$emit('validate', this.config.id)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/colors';
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/typo';
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/functions/px-to-rem';

$font-family-old: 'Roboto', sans-serif;

.selection {
  display: block;
  width: 100%;

  ::v-deep {
    .button {
      display: flex;
      border-color: $color-turquoise-surf;
      justify-content: center;
      align-items: center;
      background: $white-clr;
    }
    .radioInput ~ .text {
      border: 1px solid $color-turquoise-surf;
      border-radius: rem(20);
      padding: rem(12) rem(16);
      min-width: rem(96);
      background: $white-clr;
      color: $color-turquoise-surf;
      line-height: 1;
    }
    .buttonChecked,
    .button:hover,
    .button:active,
    .button:focus {
      .text {
        background: $color-turquoise-surf;
        color: $white-clr;
      }
    }
  }
}

.singleOption {
  margin-bottom: 0;
  color: $black-clr;
  font-family: $font-family;
  font-size: rem(16);
  line-height: rem(24);

  dt {
    margin-bottom: 0;
  }

  dd {
    margin-bottom: 0;
    font-family: $font-family-heavy;
  }
}

.descriptionText {
  color: $black-clr;
  font-family: $font-family-heavy;
  font-size: rem(20);
  line-height: rem(28);
}
</style>
